import React, { createContext, useContext, useMemo, useCallback } from 'react'
import { useSession, useOrganization } from '@clerk/clerk-react'

// =============================================================================
// Types
// =============================================================================

interface AuthContextValue {
  // Stable primitive values (safe for React dependencies)
  userId: string | null
  organizationId: string | null
  organizationSlug: string | null
  isErdosMiller: boolean

  // Stable functions (safe for React dependencies)
  getToken: () => Promise<string | null>

  // Loading states
  isLoaded: boolean
  isSignedIn: boolean

  // Raw objects (avoid using as dependencies - use primitive values above)
  user: any | null
  organization: any | null
}

export interface AuthIdentity {
  userId: string | null
  organizationId: string | null
  organizationSlug: string | null
  isErdosMiller: boolean
}

export interface AuthState {
  isLoaded: boolean
  isSignedIn: boolean
}

export interface ServiceAuthProps {
  userId: string
  orgId: string
  orgSlug: string
  getToken: () => Promise<string | null>
}

// =============================================================================
// Context
// =============================================================================

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

// =============================================================================
// Provider
// =============================================================================

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { session, isLoaded: sessionLoaded, isSignedIn } = useSession()
  const { organization, isLoaded: orgLoaded } = useOrganization()

  // Extract stable primitive values
  const userId = useMemo(() => {
    return session?.user?.id || null
  }, [session?.user?.id])

  const organizationId = useMemo(() => {
    return organization?.id || null
  }, [organization?.id])

  const organizationSlug = useMemo(() => {
    return organization?.slug || null
  }, [organization?.slug])

  const isErdosMiller = useMemo(() => {
    return organizationSlug === 'erdos-miller'
  }, [organizationSlug])

  // Create stable function reference
  const getToken = useCallback(async (): Promise<string | null> => {
    if (!session?.getToken) {
      return null
    }
    try {
      // Production: require the 'supabase' template (fail fast if missing)
      if (import.meta.env.PROD) {
        try {
          return await session.getToken({ template: 'supabase' })
        } catch (e) {
          console.error(
            'AUTH_CONTEXT: Missing or invalid "supabase" JWT template in production.',
            e
          )
          return null
        }
      }
      // Non‑production: prefer the template, fall back if missing
      try {
        return await session.getToken({ template: 'supabase' })
      } catch (templateErr) {
        console.warn(
          'AUTH_CONTEXT: supabase JWT template not found; falling back to default token',
          templateErr
        )
        return await session.getToken()
      }
    } catch (error) {
      console.warn('AUTH_CONTEXT: Failed to get token:', error)
      return null
    }
  }, [session?.getToken])

  const authValue = useMemo<AuthContextValue>(() => {
    const isLoaded = sessionLoaded && orgLoaded

    return {
      // Stable primitives (safe for dependencies)
      userId,
      organizationId,
      organizationSlug,
      isErdosMiller,

      // Stable function (safe for dependencies)
      getToken,

      // Loading states
      isLoaded,
      isSignedIn: isLoaded && isSignedIn && !!userId,

      // Raw objects (avoid as dependencies)
      user: session?.user || null,
      organization: organization || null,
    }
  }, [
    userId,
    organizationId,
    organizationSlug,
    isErdosMiller,
    getToken,
    sessionLoaded,
    orgLoaded,
    isSignedIn,
    session?.user,
    organization,
  ])

  return <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>
}

// =============================================================================
// Internal Hook (Private)
// =============================================================================

function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('Auth hooks must be used within an AuthProvider')
  }
  return context
}

// =============================================================================
// NEW GRANULAR HOOKS (Public API)
// =============================================================================

/**
 * Returns stable identity values that only change on user/organization switch.
 * Safe to use in dependency arrays - won't cause unnecessary re-renders.
 */
export function useAuthIdentity(): AuthIdentity {
  const auth = useAuthContext()

  return useMemo(
    () => ({
      userId: auth.userId,
      organizationId: auth.organizationId,
      organizationSlug: auth.organizationSlug,
      isErdosMiller: auth.isErdosMiller,
    }),
    [auth.userId, auth.organizationId, auth.organizationSlug, auth.isErdosMiller]
  )
}

/**
 * Returns reactive UI state for loading indicators and auth guards.
 * Only re-renders when loading state changes.
 */
export function useAuthState(): AuthState {
  const auth = useAuthContext()

  return useMemo(
    () => ({
      isLoaded: auth.isLoaded,
      isSignedIn: auth.isSignedIn,
    }),
    [auth.isLoaded, auth.isSignedIn]
  )
}

/**
 * Returns a function that provides fresh service auth props.
 * The returned function never changes reference - no re-renders.
 * Call the function when you need auth props for service calls.
 */
export function useServiceAuth(): () => ServiceAuthProps | null {
  const auth = useAuthContext()

  return useCallback(() => {
    if (!auth.isLoaded || !auth.isSignedIn || !auth.userId || !auth.organizationId) {
      return null
    }

    return {
      userId: auth.userId,
      orgId: auth.organizationId,
      orgSlug: auth.organizationSlug || '',
      getToken: auth.getToken,
    }
  }, []) // Empty dependencies - function reference never changes
}

/**
 * Returns the raw Clerk user object.
 * Use sparingly - prefer useAuthIdentity() for most cases.
 */
export function useAuthUser(): any | null {
  const auth = useAuthContext()
  return auth.user
}

/**
 * Returns the raw Clerk organization object.
 * Use sparingly - prefer useAuthIdentity() for most cases.
 */
export function useAuthOrganization(): any | null {
  const auth = useAuthContext()
  return auth.organization
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook that returns auth values when authentication is required.
 * Throws if user is not authenticated.
 * Use this when you need guaranteed auth values.
 */
export function useRequiredAuth(): ServiceAuthProps {
  const identity = useAuthIdentity()
  const state = useAuthState()
  const getServiceAuth = useServiceAuth()

  if (!state.isLoaded) {
    throw new Error('Auth not loaded yet')
  }

  if (!state.isSignedIn || !identity.userId || !identity.organizationId) {
    throw new Error('Authentication required')
  }

  const serviceAuth = getServiceAuth()
  if (!serviceAuth) {
    throw new Error('Failed to get service auth props')
  }

  return serviceAuth
}

/**
 * Hook that returns whether the user is authenticated.
 * Use this for conditional rendering based on auth state.
 */
export function useIsAuthenticated(): boolean {
  const { isLoaded, isSignedIn } = useAuthState()
  return isLoaded && isSignedIn
}
